import maplibregl from 'maplibre-gl';
import imageBitmapCache, {
  QuadCoordinates,
  isAlreadyMercatorProjected
} from './ImageBitmapCache';

const VERTEX_SHADER_SOURCE = `
attribute vec2 a_pos;
attribute vec2 a_uv;
uniform mat4 u_matrix;
uniform float u_wrap;
varying vec2 v_uv;

void main() {
  v_uv = a_uv;
  gl_Position = u_matrix * vec4(a_pos.x + u_wrap, a_pos.y, 0.0, 1.0);
}
`;

const FRAGMENT_SHADER_SOURCE = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
varying vec2 v_uv;
uniform sampler2D u_tex0;
uniform sampler2D u_tex1;
uniform float u_blend;
uniform float u_opacity;
uniform float u_isMercator0;
uniform float u_isMercator1;
uniform vec2 u_latBounds;
uniform vec2 u_mercYBounds;

const float HALF_PI = 1.5707963267948966;

float projectV(float vMerc, float isMercator) {
  if (isMercator > 0.5) {
    return vMerc;
  }
  float latSpan = u_latBounds.x - u_latBounds.y;
  if (abs(latSpan) < 1e-5) {
    return vMerc;
  }
  float yMerc = mix(u_mercYBounds.x, u_mercYBounds.y, vMerc);
  float latRad = 2.0 * atan(exp(yMerc)) - HALF_PI;
  return clamp((u_latBounds.x - latRad) / latSpan, 0.0, 1.0);
}

void main() {
  vec2 uv0 = vec2(v_uv.x, projectV(v_uv.y, u_isMercator0));
  vec2 uv1 = vec2(v_uv.x, projectV(v_uv.y, u_isMercator1));
  vec4 c0 = texture2D(u_tex0, uv0);
  vec4 c1 = texture2D(u_tex1, uv1);
  vec4 p0 = vec4(c0.rgb * c0.a, c0.a);
  vec4 p1 = vec4(c1.rgb * c1.a, c1.a);
  vec4 blended = mix(p0, p1, clamp(u_blend, 0.0, 1.0));
  gl_FragColor = blended * u_opacity;
}
`;

export class WebGLForecastLayer implements maplibregl.CustomLayerInterface {
  public readonly id: string;
  public readonly type: 'custom' = 'custom';
  public readonly renderingMode: '2d' = '2d';

  private map: maplibregl.Map | null = null;
  private gl: WebGLRenderingContext | WebGL2RenderingContext | null = null;
  private program: WebGLProgram | null = null;
  private vertexBuffer: WebGLBuffer | null = null;

  private aPosLoc: number = -1;
  private aUvLoc: number = -1;
  private uMatrixLoc: WebGLUniformLocation | null = null;
  private uWrapLoc: WebGLUniformLocation | null = null;
  private uTex0Loc: WebGLUniformLocation | null = null;
  private uTex1Loc: WebGLUniformLocation | null = null;
  private uBlendLoc: WebGLUniformLocation | null = null;
  private uOpacityLoc: WebGLUniformLocation | null = null;
  private uIsMercator0Loc: WebGLUniformLocation | null = null;
  private uIsMercator1Loc: WebGLUniformLocation | null = null;
  private uLatBoundsLoc: WebGLUniformLocation | null = null;
  private uMercYBoundsLoc: WebGLUniformLocation | null = null;

  private tex0: WebGLTexture | null = null;
  private tex1: WebGLTexture | null = null;
  private tex0Width: number = 0;
  private tex0Height: number = 0;
  private tex1Width: number = 0;
  private tex1Height: number = 0;
  private loadedUrl0: string = '';
  private loadedUrl1: string = '';

  private targetUrl0: string = '';
  private targetUrl1: string = '';
  private blend: number = 0.0;
  private opacity: number = 1.0;
  private minZoom: number = 0;
  private maxZoom: number = 22;
  private coordinates: QuadCoordinates;
  private vertexData = new Float32Array(24); // 6 vertices * (x, y, u, v)
  private vertexDirty: boolean = true;
  private onFallback?: (failedUrl: string) => void;

  constructor(
    id: string,
    coordinates: QuadCoordinates,
    opacity: number = 1.0,
    minZoom: number = 0,
    maxZoom: number = 22,
    onFallback?: (failedUrl: string) => void
  ) {
    this.id = id;
    this.coordinates = coordinates;
    this.opacity = opacity;
    this.minZoom = minZoom;
    this.maxZoom = maxZoom;
    this.onFallback = onFallback;
    this.rebuildVertices();
  }

  private rebuildVertices(): void {
    // coordinates order: [NW, NE, SE, SW] -> [[west, north], [east, north], [east, south], [west, south]]
    const nw = maplibregl.MercatorCoordinate.fromLngLat(this.coordinates[0]);
    const ne = maplibregl.MercatorCoordinate.fromLngLat(this.coordinates[1]);
    const se = maplibregl.MercatorCoordinate.fromLngLat(this.coordinates[2]);
    const sw = maplibregl.MercatorCoordinate.fromLngLat(this.coordinates[3]);

    // Two triangles: (NW, NE, SW) and (NE, SE, SW)
    const v = this.vertexData;
    // Triangle 1
    v[0] = nw.x; v[1] = nw.y; v[2] = 0.0; v[3] = 0.0;
    v[4] = ne.x; v[5] = ne.y; v[6] = 1.0; v[7] = 0.0;
    v[8] = sw.x; v[9] = sw.y; v[10] = 0.0; v[11] = 1.0;
    // Triangle 2
    v[12] = ne.x; v[13] = ne.y; v[14] = 1.0; v[15] = 0.0;
    v[16] = se.x; v[17] = se.y; v[18] = 1.0; v[19] = 1.0;
    v[20] = sw.x; v[21] = sw.y; v[22] = 0.0; v[23] = 1.0;

    this.vertexDirty = true;
  }

  public setCoordinates(coordinates: QuadCoordinates): void {
    const changed =
      this.coordinates[0][0] !== coordinates[0][0] ||
      this.coordinates[0][1] !== coordinates[0][1] ||
      this.coordinates[2][0] !== coordinates[2][0] ||
      this.coordinates[2][1] !== coordinates[2][1];
    if (changed) {
      this.coordinates = coordinates;
      this.rebuildVertices();
      this.map?.triggerRepaint();
    }
  }

  public setOpacity(opacity: number): void {
    if (this.opacity !== opacity) {
      this.opacity = opacity;
      this.map?.triggerRepaint();
    }
  }

  public setZoomBounds(minZoom: number, maxZoom: number): void {
    this.minZoom = minZoom;
    this.maxZoom = maxZoom;
  }

  /**
   * Updates active temporal frame pair (frame0, frame1) and normalized blend factor [0..1].
   * Reuses GPU texture handles via ping-pong pointer swap and texSubImage2D.
   */
  public setFrames(
    url0: string,
    url1: string,
    blend: number,
    opacity: number,
    coordinates?: QuadCoordinates
  ): void {
    this.opacity = opacity;
    this.blend = Math.max(0.0, Math.min(1.0, blend));

    if (coordinates) {
      const cachedBounds = imageBitmapCache.getCachedBounds(url0);
      this.setCoordinates(cachedBounds || coordinates);
    }

    // Check companion _bounds.json once per model stem for exact geographic alignment
    if (url0 && !imageBitmapCache.getCachedBounds(url0)) {
      const requestedStem = imageBitmapCache.getModelStemKey(url0);
      void imageBitmapCache.loadBoundsForWebpUrl(url0).then((exactCoords) => {
        if (
          exactCoords &&
          (this.targetUrl0 === url0 ||
            (this.targetUrl0 && imageBitmapCache.getModelStemKey(this.targetUrl0) === requestedStem))
        ) {
          this.setCoordinates(exactCoords);
        }
      });
    }

    const urlsChanged = url0 !== this.targetUrl0 || url1 !== this.targetUrl1;
    this.targetUrl0 = url0;
    this.targetUrl1 = url1;

    if (!urlsChanged) {
      this.syncTexturesFromCache();
      this.map?.triggerRepaint();
      return;
    }

    // Ping-pong optimization: if new url0 matches old loadedUrl1, swap tex0 <-> tex1 handles!
    if (url0 && url0 === this.loadedUrl1 && url1 !== this.loadedUrl0) {
      const tmpTex = this.tex0;
      this.tex0 = this.tex1;
      this.tex1 = tmpTex;

      const tmpW = this.tex0Width;
      const tmpH = this.tex0Height;
      this.tex0Width = this.tex1Width;
      this.tex0Height = this.tex1Height;
      this.tex1Width = tmpW;
      this.tex1Height = tmpH;

      const tmpUrl = this.loadedUrl0;
      this.loadedUrl0 = this.loadedUrl1;
      this.loadedUrl1 = tmpUrl;
    } else if (url1 && url1 === this.loadedUrl0 && url0 !== this.loadedUrl1) {
      // Reverse playback ping-pong optimization
      const tmpTex = this.tex0;
      this.tex0 = this.tex1;
      this.tex1 = tmpTex;

      const tmpW = this.tex0Width;
      const tmpH = this.tex0Height;
      this.tex0Width = this.tex1Width;
      this.tex0Height = this.tex1Height;
      this.tex1Width = tmpW;
      this.tex1Height = tmpH;

      const tmpUrl = this.loadedUrl0;
      this.loadedUrl0 = this.loadedUrl1;
      this.loadedUrl1 = tmpUrl;
    }

    this.syncTexturesFromCache();

    if (url0 && this.loadedUrl0 !== url0) {
      void imageBitmapCache.load(url0).then((bmp) => {
        if (!bmp && imageBitmapCache.hasFailed(url0)) {
          this.onFallback?.(url0);
          return;
        }
        if (bmp && this.targetUrl0 === url0) {
          this.uploadBitmapToSlot(0, url0, bmp);
          this.map?.triggerRepaint();
        }
      });
    }

    if (url1 && url1 !== url0 && this.loadedUrl1 !== url1) {
      void imageBitmapCache.load(url1).then((bmp) => {
        if (!bmp && imageBitmapCache.hasFailed(url1)) {
          this.onFallback?.(url1);
          return;
        }
        if (bmp && this.targetUrl1 === url1) {
          this.uploadBitmapToSlot(1, url1, bmp);
          this.map?.triggerRepaint();
        }
      });
    }

    this.map?.triggerRepaint();
  }

  private syncTexturesFromCache(): void {
    if (!this.gl) return;

    if (this.targetUrl0 && this.loadedUrl0 !== this.targetUrl0) {
      const bmp0 = imageBitmapCache.get(this.targetUrl0);
      if (bmp0) {
        this.uploadBitmapToSlot(0, this.targetUrl0, bmp0);
      }
    }

    if (this.targetUrl1 && this.targetUrl1 !== this.targetUrl0 && this.loadedUrl1 !== this.targetUrl1) {
      const bmp1 = imageBitmapCache.get(this.targetUrl1);
      if (bmp1) {
        this.uploadBitmapToSlot(1, this.targetUrl1, bmp1);
      }
    }
  }

  private createAllocatedTexture(gl: WebGLRenderingContext | WebGL2RenderingContext): WebGLTexture | null {
    const tex = gl.createTexture();
    if (!tex) return null;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    return tex;
  }

  private uploadBitmapToSlot(slot: 0 | 1, url: string, bitmap: ImageBitmap): void {
    const gl = this.gl;
    if (!gl) return;

    if (typeof WebGL2RenderingContext !== 'undefined' && gl instanceof WebGL2RenderingContext) {
      gl.bindVertexArray(null);
    }

    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 0);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, 0);

    if (slot === 0) {
      if (!this.tex0) {
        this.tex0 = this.createAllocatedTexture(gl);
      }
      if (!this.tex0) return;
      gl.bindTexture(gl.TEXTURE_2D, this.tex0);
      if (this.tex0Width === bitmap.width && this.tex0Height === bitmap.height) {
        gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, gl.RGBA, gl.UNSIGNED_BYTE, bitmap);
      } else {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, bitmap);
        this.tex0Width = bitmap.width;
        this.tex0Height = bitmap.height;
      }
      this.loadedUrl0 = url;
    } else {
      if (!this.tex1) {
        this.tex1 = this.createAllocatedTexture(gl);
      }
      if (!this.tex1) return;
      gl.bindTexture(gl.TEXTURE_2D, this.tex1);
      if (this.tex1Width === bitmap.width && this.tex1Height === bitmap.height) {
        gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, gl.RGBA, gl.UNSIGNED_BYTE, bitmap);
      } else {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, bitmap);
        this.tex1Width = bitmap.width;
        this.tex1Height = bitmap.height;
      }
      this.loadedUrl1 = url;
    }
  }

  public onAdd(map: maplibregl.Map, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
    this.map = map;
    this.gl = gl;

    const vs = gl.createShader(gl.VERTEX_SHADER);
    const fs = gl.createShader(gl.FRAGMENT_SHADER);
    if (!vs || !fs) return;

    gl.shaderSource(vs, VERTEX_SHADER_SOURCE);
    gl.compileShader(vs);

    gl.shaderSource(fs, FRAGMENT_SHADER_SOURCE);
    gl.compileShader(fs);

    const prog = gl.createProgram();
    if (!prog) return;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);

    gl.deleteShader(vs);
    gl.deleteShader(fs);

    this.program = prog;
    this.aPosLoc = gl.getAttribLocation(prog, 'a_pos');
    this.aUvLoc = gl.getAttribLocation(prog, 'a_uv');
    this.uMatrixLoc = gl.getUniformLocation(prog, 'u_matrix');
    this.uWrapLoc = gl.getUniformLocation(prog, 'u_wrap');
    this.uTex0Loc = gl.getUniformLocation(prog, 'u_tex0');
    this.uTex1Loc = gl.getUniformLocation(prog, 'u_tex1');
    this.uBlendLoc = gl.getUniformLocation(prog, 'u_blend');
    this.uOpacityLoc = gl.getUniformLocation(prog, 'u_opacity');
    this.uIsMercator0Loc = gl.getUniformLocation(prog, 'u_isMercator0');
    this.uIsMercator1Loc = gl.getUniformLocation(prog, 'u_isMercator1');
    this.uLatBoundsLoc = gl.getUniformLocation(prog, 'u_latBounds');
    this.uMercYBoundsLoc = gl.getUniformLocation(prog, 'u_mercYBounds');

    this.vertexBuffer = gl.createBuffer();
    this.vertexDirty = true;

    this.syncTexturesFromCache();
  }

  public render(gl: WebGLRenderingContext | WebGL2RenderingContext, options: any): void {
    if (!this.program || !this.vertexBuffer || this.opacity <= 0.001) return;
    if (this.map) {
      const z = this.map.getZoom();
      if (z < this.minZoom || z > Math.max(22, this.maxZoom)) return;
    }

    this.syncTexturesFromCache();

    // Ensure we have at least one valid GPU texture uploaded
    const hasTex0 = Boolean(this.tex0 && this.tex0Width > 0);
    const hasTex1 = Boolean(this.tex1 && this.tex1Width > 0);
    if (!hasTex0 && !hasTex1) return;

    // Resolve projection matrix across MapLibre v4/v5 CustomRenderMethodInput
    const matrix: Float32Array | number[] | undefined =
      options?.defaultProjectionData?.mainMatrix ||
      options?.modelViewProjectionMatrix ||
      (options instanceof Float32Array || Array.isArray(options) ? options : undefined);
    if (!matrix) return;

    if (typeof WebGL2RenderingContext !== 'undefined' && gl instanceof WebGL2RenderingContext) {
      gl.bindVertexArray(null);
    }

    gl.useProgram(this.program);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
    if (this.vertexDirty) {
      gl.bufferData(gl.ARRAY_BUFFER, this.vertexData, gl.DYNAMIC_DRAW);
      this.vertexDirty = false;
    }

    const stride = 4 * 4; // 4 floats per vertex
    if (this.aPosLoc >= 0) {
      gl.enableVertexAttribArray(this.aPosLoc);
      gl.vertexAttribPointer(this.aPosLoc, 2, gl.FLOAT, false, stride, 0);
    }
    if (this.aUvLoc >= 0) {
      gl.enableVertexAttribArray(this.aUvLoc);
      gl.vertexAttribPointer(this.aUvLoc, 2, gl.FLOAT, false, stride, 2 * 4);
    }

    // Determine effective textures & blend factor
    let activeTex0 = this.tex0;
    let activeTex1 = this.tex1;
    let activeW0 = this.tex0Width;
    let activeH0 = this.tex0Height;
    let activeW1 = this.tex1Width;
    let activeH1 = this.tex1Height;
    let effectiveBlend = this.blend;

    if (this.targetUrl0 === this.targetUrl1 || !this.targetUrl1) {
      activeTex1 = activeTex0;
      activeW1 = activeW0;
      activeH1 = activeH0;
      effectiveBlend = 0.0;
    } else if (hasTex0 && (!hasTex1 || this.loadedUrl1 !== this.targetUrl1)) {
      activeTex1 = activeTex0;
      activeW1 = activeW0;
      activeH1 = activeH0;
      effectiveBlend = 0.0;
    } else if (!hasTex0 && hasTex1) {
      activeTex0 = activeTex1;
      activeW0 = activeW1;
      activeH0 = activeH1;
      effectiveBlend = 1.0;
    }

    const isMerc0 = isAlreadyMercatorProjected(activeW0, activeH0, this.coordinates) ? 1.0 : 0.0;
    const isMerc1 = isAlreadyMercatorProjected(activeW1, activeH1, this.coordinates) ? 1.0 : 0.0;
    const clampedNorth = Math.max(-85.051129, Math.min(85.051129, this.coordinates[0][1]));
    const clampedSouth = Math.max(-85.051129, Math.min(85.051129, this.coordinates[2][1]));
    const northRad = (clampedNorth * Math.PI) / 180.0;
    const southRad = (clampedSouth * Math.PI) / 180.0;
    const yNorth = Math.log(Math.tan(Math.PI / 4.0 + northRad / 2.0));
    const ySouth = Math.log(Math.tan(Math.PI / 4.0 + southRad / 2.0));

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, activeTex0);
    gl.uniform1i(this.uTex0Loc, 0);

    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, activeTex1);
    gl.uniform1i(this.uTex1Loc, 1);

    gl.uniformMatrix4fv(this.uMatrixLoc, false, matrix as Float32Array);
    gl.uniform1f(this.uBlendLoc, effectiveBlend);
    gl.uniform1f(this.uOpacityLoc, this.opacity);
    gl.uniform1f(this.uIsMercator0Loc, isMerc0);
    gl.uniform1f(this.uIsMercator1Loc, isMerc1);
    gl.uniform2f(this.uLatBoundsLoc, northRad, southRad);
    gl.uniform2f(this.uMercYBoundsLoc, yNorth, ySouth);

    gl.disable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    // Draw primary world copy (wrap = 0) and adjacent world copies if zoomed out
    gl.uniform1f(this.uWrapLoc, 0.0);
    gl.drawArrays(gl.TRIANGLES, 0, 6);

    if (this.map && this.map.getRenderWorldCopies()) {
      const bounds = this.map.getBounds();
      if (bounds.getWest() < -170) {
        gl.uniform1f(this.uWrapLoc, -1.0);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
      }
      if (bounds.getEast() > 170) {
        gl.uniform1f(this.uWrapLoc, 1.0);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
      }
    }

    if (this.aPosLoc >= 0) gl.disableVertexAttribArray(this.aPosLoc);
    if (this.aUvLoc >= 0) gl.disableVertexAttribArray(this.aUvLoc);
    gl.activeTexture(gl.TEXTURE0);
  }

  public onRemove(_map: maplibregl.Map, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
    if (this.tex0) {
      gl.deleteTexture(this.tex0);
      this.tex0 = null;
    }
    if (this.tex1) {
      gl.deleteTexture(this.tex1);
      this.tex1 = null;
    }
    if (this.vertexBuffer) {
      gl.deleteBuffer(this.vertexBuffer);
      this.vertexBuffer = null;
    }
    if (this.program) {
      gl.deleteProgram(this.program);
      this.program = null;
    }
    this.tex0Width = 0;
    this.tex0Height = 0;
    this.tex1Width = 0;
    this.tex1Height = 0;
    this.loadedUrl0 = '';
    this.loadedUrl1 = '';
    this.gl = null;
    this.map = null;
  }
}

export default WebGLForecastLayer;

